import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Shield, Lock, ArrowRight, CheckCircle2 } from 'lucide-react';
import { ApiApi } from 'api-client';
import { PasswordResetConfirm } from 'api-client/models/PasswordResetConfirm';
import { getApiConfiguration } from '../../api/utils';
import { Button } from '../../components/ui/button';

const ConfirmPasswordReset: React.FC = () => {
    const [isSuccess, setIsSuccess] = useState(false);
    const [searchParams] = useSearchParams();
    const [newPassword1, setNewPassword1] = useState('');
    const [newPassword2, setNewPassword2] = useState('');
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Get token and uid from URL parameters
    const token = searchParams.get('token');
    const uid = searchParams.get('uid');

    useEffect(() => {
        if (!token || !uid) {
            setError('Invalid password reset link');
        }
    }, [token, uid]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setMessage('');
        setError('');

        if (newPassword1 !== newPassword2) {
            setError('Passwords do not match');
            setIsLoading(false);
            return;
        }

        try {
            const client = new ApiApi(getApiConfiguration());
            const resetData: PasswordResetConfirm = {
                token: token!,
                uid: uid!,
                newPassword1: newPassword1,
                newPassword2: newPassword2
            };
            
            await client.apiAuthPasswordResetConfirmCreate2({
                token: token!,
                uidb64: uid!,
                passwordResetConfirm: resetData
            });

            setIsSuccess(true);
            setMessage('Your password has been reset successfully!');
        } catch (err: any) {
            setError(err.message || 'Failed to reset password. Please try again.');
            console.error('Error:', err);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#0A0F1C] flex items-center justify-center p-6">
            <div className="w-full max-w-md">
                <div className="bg-[#0F1629] rounded-xl border border-gray-800/50 p-8">
                    {/* Header */}
                    <div className="flex items-center justify-center mb-8">
                        <Shield className="h-10 w-10 text-blue-500" />
                        <h1 className="text-2xl font-bold text-white ml-3">Flawtrack</h1>
                    </div>

                    {/* Title */}
                    <div className="text-center mb-8">
                        <h2 className="text-xl font-semibold text-white">Reset Password</h2>
                        <p className="text-sm text-gray-400 mt-1">Enter your new password</p>
                    </div>

                    {isSuccess ? (
                        <div className="space-y-6 text-center">
                            <div className="flex flex-col items-center justify-center space-y-4">
                                <CheckCircle2 className="w-16 h-16 text-green-500" />
                                <h3 className="text-xl font-semibold text-white">Password Reset Complete!</h3>
                                <p className="text-gray-400">{message}</p>
                            </div>
                            <Link 
                                to="/login"
                                className="inline-flex items-center justify-center w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                            >
                                <ArrowRight className="w-4 h-4 mr-2" />
                                Continue to Login
                            </Link>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-6">

                        <div>
                            <label htmlFor="newPassword1" className="block text-sm font-medium text-gray-300 mb-2">
                                New Password
                            </label>
                            <input
                                type="password"
                                id="newPassword1"
                                value={newPassword1}
                                onChange={(e) => setNewPassword1(e.target.value)}
                                className="w-full px-3 py-2 bg-[#1A2235] border border-gray-800 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Enter new password"
                                required
                                minLength={8}
                            />
                        </div>

                        <div>
                            <label htmlFor="newPassword2" className="block text-sm font-medium text-gray-300 mb-2">
                                Confirm New Password
                            </label>
                            <input
                                type="password"
                                id="newPassword2"
                                value={newPassword2}
                                onChange={(e) => setNewPassword2(e.target.value)}
                                className="w-full px-3 py-2 bg-[#1A2235] border border-gray-800 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Confirm new password"
                                required
                                minLength={8}
                            />
                        </div>

                        {message && (
                            <div className="p-4 bg-blue-500/10 text-blue-500 rounded-lg border border-blue-500/20">
                                {message}
                            </div>
                        )}

                        {error && (
                            <div className="p-4 bg-red-500/10 text-red-500 rounded-lg border border-red-500/20">
                                {error}
                            </div>
                        )}

                        <Button
                            type="submit"
                            disabled={isLoading || !token || !uid}
                            className="w-full"
                        >
                            <Lock className="w-4 h-4 mr-2" />
                            {isLoading ? 'Resetting...' : 'Reset Password'}
                            <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>

                        {!isSuccess && (
                            <Link 
                                to="/login"
                                className="inline-flex items-center justify-center w-full px-4 py-2 bg-transparent border border-gray-800 text-white rounded-lg hover:bg-gray-800/50 transition-colors"
                            >
                                <ArrowRight className="w-4 h-4 mr-2" />
                                Back to Login
                            </Link>
                        )}
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ConfirmPasswordReset;
